import React, { useRef, useState } from "react";
import { AiOutlineEye, AiOutlineEyeInvisible } from "react-icons/ai";
import { MdCheck, MdClose, MdMail, MdLock } from "react-icons/md";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "react-toastify";

function RememberMeCheckbox({ isChecked, handleCheckboxChange }) {
	return (
		<div
			className="flex items-center gap-2 cursor-pointer group"
			onClick={handleCheckboxChange}
		>
			<div
				className={`w-5 h-5 rounded border-2 flex items-center justify-center transition-all duration-200 ${isChecked ? "bg-green-600 border-green-600" : "bg-white border-gray-300 group-hover:border-green-400"}`}
			>
				{isChecked && <MdCheck className="text-white text-sm" />}
			</div>
			<span className="text-sm text-gray-600 select-none">Ingat Saya</span>
		</div>
	);
}

function Login({ closePopup, onLoginSuccess }) {
	const rememberedEmail = localStorage.getItem("rememberedEmail") || "";
	const [email, setEmail] = useState(rememberedEmail);
	const [password, setPassword] = useState("");
	const [showPassword, setShowPassword] = useState(false);
	const [loading, setLoading] = useState(false);
	const [isChecked, setIsChecked] = useState(!!rememberedEmail);
	const [emailError, setEmailError] = useState(false);
	const [passwordError, setPasswordError] = useState(false);
	const [useFetch, setUseFetch] = useState(true);
	const navigate = useNavigate();
	const popupRef = useRef(null);

	const handleCheckboxChange = () => {
		setIsChecked(!isChecked);
	};

	const handleOutsideClick = (e) => {
		if (popupRef.current && !popupRef.current.contains(e.target)) {
			closePopup?.();
		}
	};

	const handleSubmit = async (e) => {
		e.preventDefault();
		setLoading(true);
		setEmailError(false);
		setPasswordError(false);

		if (!email || !password) {
			if (!email) setEmailError(true);
			if (!password) setPasswordError(true);
			setLoading(false);
			return;
		}

		const data = { email, password, isChecked };

		if (useFetch) {
			fetch("/_api/main/login", {
				method: "POST",
				headers: { "Content-Type": "application/json" },
				body: JSON.stringify(data),
			})
				.then((res) => res.json())
				.then((result) => {
					if (result.success) {
						window.location.href = result.redirect_uri;
						return;
					}
					if (result.error) {
						setPasswordError(true);
						setEmailError(true);
						toast.error(result.error);
					}
				})
				.catch(() => {
					setPasswordError(true);
					setEmailError(true);
					toast.error("Gagal melakukan login");
				})
				.finally(() => setLoading(false));
		} else {
			let loginData = JSON.stringify({
				username: email,
				password: password,
			});

			let config = {
				method: "post",
				url: `/api_w/users/login`,
				headers: {
					"Content-Type": "application/json",
					db_name: process.env.REACT_APP_DB_NAME,
				},
				data: loginData,
			};

			try {
				const response = await axios.request(config);
				if (response.status === 200) {
					const { token, user } = response.data;
					localStorage.setItem("token", token);
					localStorage.setItem("id", user.id);
					localStorage.setItem("role", user.role);
					if (isChecked) {
						localStorage.setItem("rememberedEmail", email);
					} else {
						localStorage.removeItem("rememberedEmail");
						localStorage.removeItem("rememberedPassword");
					}

					toast.success("Berhasil Login");
					if (user.role === "superadmin" || user.role === "admin") {
						fetch("/_api/main/login", {
							method: "POST",
							headers: { "Content-Type": "application/json" },
							body: JSON.stringify({ email, password, isChecked }),
						})
							.then((r) => r.json())
							.then((res) => {
								if (res.success && res.redirect_uri) {
									const iframe = document.createElement("iframe");
									iframe.style.display = "none";
									iframe.src = res.redirect_uri;
									iframe.onload = () => iframe.remove();
									iframe.onerror = () => iframe.remove();
									document.body.appendChild(iframe);
								}
							})
							.catch(() => {});
						navigate("/admin");
					} else {
						onLoginSuccess?.();
						closePopup?.();
					}
				}
			} catch (error) {
				toast.error(error.response?.data?.message || "Gagal Login");
				setPasswordError(true);
			} finally {
				setLoading(false);
			}
		}
	};

	return (
		<div
			onClick={handleOutsideClick}
			className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 backdrop-blur-sm px-4"
		>
			<div
				ref={popupRef}
				className="relative w-full max-w-[420px] bg-white rounded-[2.5rem] shadow-2xl overflow-hidden animate-fade-in-up"
			>
				{/* Close Button */}
				<button
					onClick={closePopup}
					className="absolute top-6 right-6 p-3 rounded-full hover:bg-gray-100 transition-colors duration-200 z-50"
				>
					<MdClose className="text-gray-400 text-xl" />
				</button>

				{/* Content */}
				<div className="px-8 pt-12 pb-10">
					<div className="text-center mb-10">
						<div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-bold mb-4 bg-green-50 text-green-700 border border-green-200">
							<span className="w-2 h-2 rounded-full bg-green-500 animate-pulse" />
							Mode: {useFetch ? "Geschool" : "Admin Dashboard"}
						</div>
						<h2 className="text-2xl font-bold text-gray-800 mb-2">
							Selamat Datang
						</h2>
						<p className="text-gray-500 text-sm">
							{useFetch
								? "Masuk dengan akun Geschool Anda"
								: "Masuk dengan akun Admin Dashboard"}
						</p>
					</div>

					<form onSubmit={handleSubmit} className="space-y-5">
						{/* Email Input */}
						<div className="space-y-1.5">
							<label className="text-sm font-bold text-gray-700 ml-1">
								Email / Username
							</label>
							<div
								className={`relative flex items-center transition-all duration-300 ${
									emailError
										? "ring-2 ring-red-500"
										: "ring-1 ring-gray-200 focus-within:ring-2 focus-within:ring-green-500"
								} rounded-2xl bg-gray-50/50 p-1`}
							>
								<div className="pl-3 pr-2 text-gray-400">
									<MdMail className="text-xl" />
								</div>
								<input
									type="text"
									placeholder="Masukkan email / username"
									className="w-full bg-transparent px-2 py-3 text-sm text-gray-800 outline-none placeholder:text-gray-400"
									value={email}
									onChange={(e) => setEmail(e.target.value)}
								/>
							</div>
						</div>
						{emailError && (
							<p className="text-xs text-red-500 ml-1">Wajib diisi</p>
						)}

						{/* Password Input */}
						<div className="space-y-1.5">
							<label className="text-sm font-bold text-gray-700 ml-1">
								Password
							</label>
							<div
								className={`relative flex items-center transition-all duration-300 ${
									passwordError
										? "ring-2 ring-red-500"
										: "ring-1 ring-gray-200 focus-within:ring-2 focus-within:ring-green-500"
								} rounded-2xl bg-gray-50/50 p-1`}
							>
								<div className="pl-3 pr-2 text-gray-400">
									<MdLock className="text-xl" />
								</div>
								<input
									type={showPassword ? "text" : "password"}
									placeholder="Masukkan password"
									className="w-full bg-transparent px-2 py-3 text-sm text-gray-800 outline-none placeholder:text-gray-400"
									value={password}
									onChange={(e) => setPassword(e.target.value)}
								/>
								<button
									type="button"
									className="px-3 text-gray-400 hover:text-green-600 transition-colors"
									onClick={() => setShowPassword(!showPassword)}
								>
									{showPassword ? (
										<AiOutlineEyeInvisible className="text-xl" />
									) : (
										<AiOutlineEye className="text-xl" />
									)}
								</button>
							</div>
							{passwordError && (
								<p className="text-xs text-red-500 ml-1">
									Wajib diisi atau tidak sesuai
								</p>
							)}
						</div>

						<div className="flex items-center justify-between pt-1">
							<RememberMeCheckbox
								isChecked={isChecked}
								handleCheckboxChange={handleCheckboxChange}
							/>
							<button
								type="button"
								className="text-xs font-bold text-green-600 hover:text-green-700"
							>
								Lupa Password?
							</button>
						</div>

						<button
							type="submit"
							disabled={loading}
							className="w-full bg-green-600 hover:bg-green-700 disabled:bg-gray-300 text-white font-bold py-4 rounded-2xl shadow-lg shadow-green-600/20 transition-all duration-300 transform hover:-translate-y-1"
						>
							{loading ? (
								<div className="flex items-center justify-center gap-2">
									<div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
									<span>Memproses...</span>
								</div>
							) : (
								"Masuk Ke Akun"
							)}
						</button>

						<div className="text-center pt-4">
							<p className="text-sm text-gray-500">
								Belum punya akun?{" "}
								<span className="text-green-600 font-bold cursor-pointer hover:underline">
									Hubungi Admin
								</span>
							</p>
						</div>

						{/* Toggle Login Mode */}
						<div className="pt-6 border-t border-gray-100 mt-6 text-center">
							<button
								type="button"
								onClick={() => setUseFetch(!useFetch)}
								className="text-[10px] uppercase tracking-widest font-bold text-gray-400 hover:text-green-600 transition-colors"
							>
								Ubah ke Mode {useFetch ? "Admin Dashboard" : "Geschool"}
							</button>
						</div>
					</form>
				</div>
			</div>
		</div>
	);
}

export default Login;
